import * as React from "react";
import { useState } from "react";
import apiClient from "@/api-client";
import { SERVICE_TYPE_OPTIONS } from "@/constants";
import { FormControl, Grid, InputLabel, OutlinedInput } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Controller, FormProvider, useForm } from "react-hook-form";

import { pluginsKeys, usePluginServices } from "@/hooks/api/use-plugins";
import { useCreateService, useService, useUpdateService } from "@/hooks/api/use-services";
import { ComponentLoader } from "@/components/componentLoader/component-loader";
import { CustomDrawer } from "@/components/custom-drawer";
import { CustomFormHelperError } from "@/components/customFormHelper/custom-form-helper-error";
import { DynamicField } from "@/components/dynamicField/dynamic-field";
import { DynamicFieldType } from "@/components/dynamicField/types";

export function CreateService({
	isCreateServiceDrawerOpen,
	onCreateDrawerClose,
	serviceId,
}: {
	isCreateServiceDrawerOpen: boolean;
	onCreateDrawerClose: () => void;
	serviceId: string | null;
}): React.JSX.Element {
	const isEdit = !!serviceId;
  
	const { data: service, isLoading: isServiceLoading } = useService(serviceId || "");
	const [selectedServiceType, setSelectedServiceType] = useState<string>("");
	const [selectedPlugin, setSelectedPlugin] = useState<string>("");
  
	const [pluginServiceFieldsData, setPluginServiceFieldsData] = useState<any[]>([]);
	const [isPluginServiceFieldsLoading, setIsPluginServiceFieldsLoading] = useState(false);
  
	const { data: serviceTypeSettingsData = [], isFetching: isServiceTypeSettingsLoading } =
  usePluginServices(selectedServiceType);
  
	const queryClient = useQueryClient();
	const createServiceMutation = useCreateService();
	const updateServiceMutation = useUpdateService();

	const isLoading = isServiceLoading || isServiceTypeSettingsLoading || isPluginServiceFieldsLoading;

	const methods = useForm<any>({
		defaultValues: {
			title: "",
			type: "",
			pluginId: "",
			settings: {},
		},
	});

	const {
		handleSubmit,
		reset,
		control,
		setError,
		setValue,
		getValues,
		formState: { isSubmitting },
	} = methods;

	const fetchPluginServiceFields = async (serviceType: string, plugin: string, withSettingsReset: boolean = false) => {
		setIsPluginServiceFieldsLoading(true);

		try {
			const pluginServiceFields = await queryClient.fetchQuery({
				queryKey: pluginsKeys.serviceFields(serviceType, plugin),
				queryFn: async () => {
					const response = await apiClient.get(`/plugin-services/${serviceType}/${plugin}`);
					return response.data;
				},
			});

			setPluginServiceFieldsData(pluginServiceFields);

			if (withSettingsReset) {
				setValue(
					"settings",
					Object.fromEntries(pluginServiceFields?.map((field: any) => [field.name, field.value]) || [])
				);
			}
		} catch (error) {
			console.error("Failed to fetch plugin service fields:", error);
		} finally {
			setIsPluginServiceFieldsLoading(false);
		}
	};

	React.useLayoutEffect(() => {
		if (service && isEdit && isCreateServiceDrawerOpen) {
			const fetchFields = async () => {
				await fetchPluginServiceFields(service.type, service.plugin.id);
			};
			reset({
				title: service.title,
				type: service.type,
				pluginId: service.plugin.id,
				settings: service.settings,
			});
			setSelectedServiceType(service.type);
			setSelectedPlugin(service.plugin.id);
			fetchFields();
		} else {
			reset({
				title: "",
				type: "",
				pluginId: "",
				settings: {},
			});
			setSelectedPlugin("");
			setSelectedServiceType("");
		}
	}, [service, isEdit, reset, isCreateServiceDrawerOpen, isCreateServiceDrawerOpen]);

	const handleChangeServiceType = (value: string) => {
		const settings = getValues("settings");

		if (settings) {
			for (const key of Object.keys(settings)) {
				setValue(`settings.${key}`, null);
			}
		}
		setValue("pluginId", "");
		setValue("settings", {});
		setSelectedPlugin("");
		setSelectedServiceType(value);
	};

	const handleChangePlugin = async (value: string) => {
		const settings = getValues("settings");

		if (settings) {
			for (const key of Object.keys(settings)) {
				setValue(`settings.${key}`, null);
			}
		}
		setValue("settings", {});
		setSelectedPlugin(value);

		if (value) {
			await fetchPluginServiceFields(selectedServiceType, value, true);
		}
	};

	const onSubmitForm = async (data: any) => {
		try {
			await (isEdit && service?.id
				? updateServiceMutation.mutateAsync({ id: service.id, data })
				: createServiceMutation.mutateAsync(data));

			onCreateDrawerClose();
		} catch (error: any) {
			const errorMessages = error?.response?.data?.message;
			if (!Array.isArray(errorMessages)) {
				return;
			}

			const errorMessage = errorMessages[0];
			setError(errorMessage.split(" ")[0], { message: errorMessage });
		}
	};

	return (
		<React.Fragment>
			<CustomDrawer
				open={isCreateServiceDrawerOpen}
				onClose={onCreateDrawerClose}
				title={isEdit ? "Edit Service" : "Create Service"}
				onSubmit={handleSubmit(onSubmitForm)}
				isSubmitButtonDisabled={isSubmitting || createServiceMutation.isPending || updateServiceMutation.isPending}
				submitButtonText={isEdit ? "Update Service" : "Create Service"}
				width="580px"
			>
				<ComponentLoader loading={isLoading} />
				<FormProvider {...methods}>
					<Grid container spacing={2}>
						<Grid
							size={{
								md: 12,
								xs: 12,
							}}
						>
							{" "}
							<Controller
								control={control}
								name="title"
								rules={{
									required: "Title is required",
									minLength: {
										value: 2,
										message: "Title must be at least 2 characters",
									},
								}}
								render={({ field, fieldState }) => (
									<FormControl error={Boolean(fieldState.error)} fullWidth required>
										<InputLabel>Title</InputLabel>
										<OutlinedInput {...field} placeholder="Enter title" />
										{fieldState.error && <CustomFormHelperError text={fieldState.error.message || ""} />}
									</FormControl>
								)}
							/>
						</Grid>

						<Grid
							size={{
								md: 12,
								xs: 12,
							}}
						>
							<Controller
								control={control}
								name="type"
								disabled={isEdit}
								rules={{
									required: "Type is required",
								}}
								render={({ field, fieldState }) => (
									<FormControl error={Boolean(fieldState.error)} fullWidth required>
										<DynamicField
											control={control}
											type={DynamicFieldType.SELECT}
											name={field.name}
											label="Type"
											onChange={(value) => handleChangeServiceType(value)}
											disabled={isEdit}
											dynamicField={{
												name: field.name,
												required: true,
												label: "Type",
												type: DynamicFieldType.SELECT,
												placeholder: "Select type",
												options: SERVICE_TYPE_OPTIONS,
											}}
										/>
									</FormControl>
								)}
							/>
						</Grid>

						{selectedServiceType && serviceTypeSettingsData?.length > 0 && !isServiceTypeSettingsLoading &&(
							<Grid
								size={{
									md: 12,
									xs: 12,
								}}
							>
								<Controller
									control={control}
									name="pluginId"
									rules={{
										required: "Plugin is required",
									}}
									render={({ field, fieldState }) => (
										<FormControl error={Boolean(fieldState.error)} fullWidth required>
											<DynamicField
												control={control}
												type={DynamicFieldType.SELECT}
												name={field.name}
												label="Plugin"
												onChange={(value) => handleChangePlugin(value)}
												dynamicField={{
													name: field.name,
													required: true,
													label: "Plugin",
													type: DynamicFieldType.SELECT,
													placeholder: "Select plugin",
													options: serviceTypeSettingsData?.map((service: any) => ({
														value: service.id,
														label: service.title,
													})),
												}}
											/>
										</FormControl>
									)}
								/>
							</Grid>
						)}

						{selectedPlugin && pluginServiceFieldsData?.length > 0 && !isPluginServiceFieldsLoading && (
							<>
								{pluginServiceFieldsData?.map((field: any) => (
									<Grid
										key={`${pluginServiceFieldsData}-${field.name}`}
										size={{
											md: 12,
											xs: 12,
										}}
									>
										<DynamicField
											control={control}
											type={field.type}
											name={`settings.${field.name}`}
											label={field.label}
											dynamicField={field}
											formName="settings"
										/>
									</Grid>
								))}
							</>
						)}
					</Grid>
				</FormProvider>
			</CustomDrawer>
		</React.Fragment>
	);
}
